import type { PropsWithChildren, ReactElement } from 'react'

import {
  type IBaseTextProps,
  IconAttention,
  Text,
  WithTooltip,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { useFormatOrderItemStatus } from '@webapp/hooks/useFormatOrderItemStatus'
import { type OrderItem } from '@webapp/models'
import { type OrderItemStatus, OrderItemStatuses } from '@webapp/models/enums'
import messages from './messages'
import { OrderItemStatusLabelStyled } from './styles/OrderItemStatusLabelStyled'

export interface IOrderItemStatusLabelProps {
  status: OrderItemStatus
  purchaseOrderNumber?: OrderItem['purchase_order_number']
  showOrderConfirmationWarning?: boolean
  children?: (props: IBaseTextProps) => ReactElement
}

const getOrderStatusColor = (orderItemStatus: OrderItemStatus): string => {
  switch (orderItemStatus) {
    case OrderItemStatuses.APPROVED:
    case OrderItemStatuses.PURCHASED:
    case OrderItemStatuses.RECEIVED:
      return 'success'
    case OrderItemStatuses.PENDING:
    case OrderItemStatuses.PENDING_PURCHASE:
    case OrderItemStatuses.PARTIALLY_RECEIVED:
    case OrderItemStatuses.DRAFT:
      return 'warning'
    case OrderItemStatuses.REJECTED:
    case OrderItemStatuses.DENIED:
      return 'danger'
    default:
      return 'textPrimary'
  }
}

export const OrderItemStatusLabel = ({
  status,
  purchaseOrderNumber,
  showOrderConfirmationWarning,
  children,
}: PropsWithChildren<IOrderItemStatusLabelProps>) => {
  const intl = useProcurifyIntl()
  const formatOrderItemStatus = useFormatOrderItemStatus()

  const textProps: PropsWithChildren<IBaseTextProps> = {
    color: getOrderStatusColor(status),
    children: formatOrderItemStatus(status),
  }

  let label

  if (children) {
    label = children(textProps)
  } else {
    label = <Text {...textProps} />
  }

  return (
    <OrderItemStatusLabelStyled>
      <WithTooltip
        title={intl.formatMessage(messages.purchaseOrderTooltip, {
          purchaseOrderNumber,
        })}
        placement='top'
        isDisabled={!purchaseOrderNumber}
      >
        <WithTooltip.Wrapper>{label}</WithTooltip.Wrapper>
      </WithTooltip>
      {showOrderConfirmationWarning && (
        <WithTooltip
          title={intl.formatMessage(messages.punchoutOrderConfirmationTooltip)}
          placement='top'
        >
          <WithTooltip.Wrapper>
            <IconAttention
              ariaLabel={intl.formatMessage(
                messages.punchoutOrderConfirmationIconAriaLabel
              )}
            />
          </WithTooltip.Wrapper>
        </WithTooltip>
      )}
    </OrderItemStatusLabelStyled>
  )
}

OrderItemStatusLabel.displayName = 'OrderItemStatusLabel'
